import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Search, Sparkles, Sun, Moon, User, LogOut, Crown } from "lucide-react";
import { useTheme } from "../lib/theme";
import { useAuth } from "../lib/auth";
import GlobalSearch from "./GlobalSearch";

const NAV_GROUPS = [
  {
    label: "Frameworks",
    intro: "Risk frameworks indexed",
    description: "32+ frameworks with complexity, certification & time-to-implement comparison.",
    sections: [
      { title: "Universal & ERM", items: [
        { name: "ISO 31000", to: "/frameworks/iso-31000", desc: "Risk Management Guidelines" },
        { name: "COSO ERM", to: "/frameworks/coso-erm", desc: "Enterprise Risk Management" },
        { name: "FERMA RMS", to: "/frameworks/ferma", desc: "European standard" },
        { name: "M_o_R", to: "/frameworks/m-o-r", desc: "Management of Risk" },
      ]},
      { title: "Cyber & IT", items: [
        { name: "ISO 27001", to: "/frameworks/iso-27001", desc: "ISMS standard" },
        { name: "NIST RMF", to: "/frameworks/nist-rmf", desc: "SP 800-37" },
        { name: "COBIT 2019", to: "/frameworks/cobit-2019", desc: "IT governance" },
        { name: "SOC 2", to: "/frameworks/soc-2", desc: "Trust services criteria" },
      ]},
      { title: "Sectoral", items: [
        { name: "Basel III / IV", to: "/frameworks/basel-iii", desc: "Banking capital" },
        { name: "Solvency II", to: "/frameworks/solvency-ii", desc: "Insurance prudential" },
        { name: "DORA", to: "/frameworks/dora", desc: "EU operational resilience" },
        { name: "TCFD / ISSB", to: "/frameworks/tcfd", desc: "Climate disclosure" },
      ]},
    ],
    footer: { text: "View all 32 frameworks", to: "/frameworks" }
  },
  {
    label: "Singapore Hub",
    intro: "Regulators · MAS · PDPA",
    description: "Deep-dive control catalogues for every Singapore FI regulator.",
    sections: [
      { title: "MAS Notices & Guidelines", items: [
        { name: "MAS TRM Guidelines", to: "/singapore/mas-trm", desc: "Tech Risk Management" },
        { name: "MAS Notice 655", to: "/singapore/mas-notice-655", desc: "Cyber Hygiene" },
        { name: "FAA & SFA", to: "/singapore/faa-sfa", desc: "Advisers & capital markets" },
        { name: "Insurance Act", to: "/singapore/ifa", desc: "RBC 2 / ORSA" },
      ]},
      { title: "Privacy & Cyber", items: [
        { name: "PDPA 2012", to: "/singapore/pdpa-2012", desc: "9 obligations + breach" },
        { name: "Cybersecurity Act", to: "/singapore/cybersecurity-act", desc: "CSA / CCoP 2.0" },
        { name: "MindForge AI", to: "/singapore/mindforge", desc: "AI risk consortium" },
        { name: "Payment Services Act", to: "/singapore/payment-services-act", desc: "PSA / DPT" },
      ]},
      { title: "Intel & Templates", items: [
        { name: "CSRS Scorer", to: "/tools/csrs-calculator", desc: "Composite SG Risk Score" },
        { name: "Breach Playbook", to: "/singapore/breach-playbook", desc: "PDPA + MAS + CSA" },
        { name: "Templates Library", to: "/singapore/templates", desc: "RoPA, IR plan, BCP" },
        { name: "Regulator Overlap", to: "/singapore/overlap", desc: "MAS · PDPC · CSA" },
      ]},
    ],
    footer: { text: "Open Singapore Hub →", to: "/singapore" }
  },
  {
    label: "Money Flow",
    intro: "Cross-border intelligence",
    description: "S$NEER, MAS policy, payment corridors and Project Nexus.",
    sections: [
      { title: "Monetary Policy", items: [
        { name: "S$NEER Tracker", to: "/money-flow/sneer", desc: "Live policy band" },
        { name: "MAS MPS Brief", to: "/money-flow/mps", desc: "Next: April 2026" },
        { name: "FX Pulse", to: "/money-flow/fx", desc: "SGD crosses" },
      ]},
      { title: "Payment Corridors", items: [
        { name: "UPI ⇄ PayNow", to: "/money-flow/upi-paynow", desc: "Cross-border real-time" },
        { name: "ASEAN-5 Connectivity", to: "/money-flow/asean", desc: "DuitNow · PromptPay · QRIS" },
        { name: "Project Nexus", to: "/money-flow/nexus", desc: "BIS multilateral layer" },
      ]},
      { title: "Crypto & DPT", items: [
        { name: "DPT Licensing Map", to: "/money-flow/dpt", desc: "Singapore crypto regime" },
        { name: "Stablecoin Regime", to: "/money-flow/stablecoins", desc: "MAS SCS framework" },
        { name: "Project Guardian", to: "/money-flow/guardian", desc: "Tokenised assets pilot" },
      ]},
    ],
    footer: { text: "Open Money Flow Hub →", to: "/money-flow" }
  },
  {
    label: "Tools",
    intro: "Interactive assessors",
    description: "Free practitioner tools. No login, no email.",
    sections: [
      { title: "Quick (under 5 min)", items: [
        { name: "PDPA Quick Quiz", to: "/tools/pdpa-quiz", desc: "60 sec · 5 questions" },
        { name: "Cost-of-Non-Compliance", to: "/tools/non-compliance-cost", desc: "S$ exposure" },
        { name: "Framework Matcher", to: "/tools/framework-matcher", desc: "Top 4 in 2 min" },
      ]},
      { title: "Automation", items: [
        { name: "Workflow Templates", to: "/workflows", desc: "12 pre-built · save · run · share" },
        { name: "Singapore Trends", to: "/trending", desc: "Live Google search interest" },
      ]},
      { title: "Deep Scorers", items: [
        { name: "CSRS Calculator", to: "/tools/csrs-calculator", desc: "Composite SG Risk Score" },
        { name: "PDPA Readiness", to: "/tools/pdpa-scorer", desc: "10-question gap scan" },
        { name: "MAS TRM Readiness", to: "/tools/mas-trm-readiness", desc: "9-domain heat-map" },
        { name: "AI Risk Maturity", to: "/tools/ai-risk-maturity", desc: "Veritas FEAT aligned" },
      ]},
      { title: "Calendars & Feeds", items: [
        { name: "MAS Deadline Calendar", to: "/tools/mas-calendar", desc: "Outlook / Google .ics" },
        { name: "All Tools", to: "/tools", desc: "Browse the toolbox" },
      ]},
    ],
    footer: { text: "View all 8 tools →", to: "/tools" }
  },
  {
    label: "Resources",
    intro: "Glossary · Webinars · Community",
    description: "Definitions, monthly sessions and the practitioner forum.",
    sections: [
      { title: "Reference", items: [
        { name: "Risk Glossary", to: "/glossary", desc: "300+ AI-curated terms" },
        { name: "Singapore Glossary", to: "/glossary?cat=singapore", desc: "MAS · PDPA · CCoP" },
      ]},
      { title: "Learning", items: [
        { name: "Webinars · live monthly", to: "/webinars", desc: "Singapore Risk Desk" },
        { name: "RiskPedia Shorts", to: "/shorts", desc: "60-sec explainers" },
        { name: "Newsletters", to: "/newsletter", desc: "Weekly SG intel" },
      ]},
      { title: "Community", items: [
        { name: "Practitioner Forum", to: "/community", desc: "Admin-curated launch" },
        { name: "WhatsApp Alerts", to: "/community#whatsapp", desc: "MAS / PDPC pings" },
      ]},
    ],
    footer: { text: "Browse resources →", to: "/resources" }
  },
  {
    label: "About",
    intro: "About · Partners · Contact",
    flat: true,
    items: [
      { name: "About RiskPedia SG", to: "/about" },
      { name: "Partners", to: "/partners" },
      { name: "Press", to: "/press" },
      { name: "Contact", to: "/contact" },
    ]
  },
];

function MegaPanel({ group, onClose }) {
  if (group.flat) {
    return (
      <div className="megamenu" style={{ gridTemplateColumns: "240px 1fr", minWidth: 480 }} onMouseLeave={onClose}>
        <div>
          <div className="overline">Menu</div>
          <h4 className="font-display text-xl mt-2" style={{ color: "var(--rp-primary)" }}>{group.label}</h4>
          <p className="text-xs text-slate-500 mt-3">{group.intro}</p>
        </div>
        <div className="grid grid-cols-2 gap-1">
          {group.items.map(it => (
            <Link key={it.to} to={it.to} className="megamenu-item" data-testid={`nav-${it.name.toLowerCase().replace(/\s+/g, "-")}`} onClick={onClose}>
              <strong>{it.name}</strong>
            </Link>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="megamenu" onMouseLeave={onClose}>
      <div>
        <div className="overline">{group.intro}</div>
        <h4 className="font-display text-xl mt-2" style={{ color: "var(--rp-primary)" }}>{group.label}</h4>
        <p className="text-xs text-slate-500 mt-3 leading-relaxed">{group.description}</p>
        {group.footer && (
          <Link to={group.footer.to} className="mt-6 inline-flex items-center gap-1 text-xs font-medium" style={{ color: "var(--rp-accent)" }} onClick={onClose}>
            {group.footer.text}
          </Link>
        )}
      </div>
      <div className="grid grid-cols-3 gap-6">
        {group.sections.map(sec => (
          <div key={sec.title}>
            <div className="overline-muted mb-3">{sec.title}</div>
            <div className="flex flex-col gap-1">
              {sec.items.map(it => (
                <Link key={it.to} to={it.to} className="megamenu-item" onClick={onClose} data-testid={`nav-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <strong>{it.name}</strong>
                  <span>{it.desc}</span>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UserMenu({ user, logout }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseLeave={() => setOpen(false)}>
      <button onClick={() => setOpen(v => !v)} className="persona-btn" data-testid="user-menu-trigger">
        <User size={14} />
        <span className="max-w-[120px] truncate">{user.name || user.email}</span>
        {user.is_pro && <Crown size={12} style={{ color: "var(--rp-accent)" }} />}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 rp-card p-2 fade-in z-50" data-testid="user-menu">
          <Link to="/profile" className="block px-3 py-2 text-sm rounded-sm hover:bg-slate-50" onClick={() => setOpen(false)}>My account</Link>
          <Link to="/team" className="block px-3 py-2 text-sm rounded-sm hover:bg-slate-50" onClick={() => setOpen(false)} data-testid="nav-team">Team workspace</Link>
          {!user.is_pro && <Link to="/pricing" className="block px-3 py-2 text-sm rounded-sm hover:bg-slate-50 font-medium" style={{ color: "var(--rp-accent)" }} onClick={() => setOpen(false)}>Upgrade to Pro</Link>}
          {user.role === "admin" && <Link to="/admin" className="block px-3 py-2 text-sm rounded-sm hover:bg-slate-50" onClick={() => setOpen(false)}>Admin panel</Link>}
          <button onClick={() => { setOpen(false); logout(); }} className="w-full text-left block px-3 py-2 text-sm rounded-sm hover:bg-slate-50 text-rose-600" data-testid="nav-logout"><LogOut size={12} className="inline mr-1" /> Sign out</button>
        </div>
      )}
    </div>
  );
}

function MobileMenu({ onOpenAdvisor, theme, toggle }) {
  return (
    <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="nav-mobile-menu">
      <div className="container-rp py-4 max-h-[80vh] overflow-y-auto">
        {NAV_GROUPS.map(g => (
          <details key={g.label} className="border-b border-slate-100 py-2">
            <summary className="cursor-pointer text-sm font-medium py-2 flex justify-between items-center">
              {g.label}
              <ChevronDown size={14} />
            </summary>
            <div className="pl-2 pb-2 flex flex-col gap-1">
              {(g.flat ? g.items : g.sections.flatMap(s => s.items)).map(it => (
                <Link key={it.to} to={it.to} className="text-sm text-slate-600 py-1.5">{it.name}</Link>
              ))}
            </div>
          </details>
        ))}
        <button onClick={onOpenAdvisor} className="btn-accent w-full justify-center mt-4">
          <Sparkles size={14} /> Ask AI Advisor
        </button>
        <button onClick={toggle} className="btn-outline w-full justify-center mt-2" data-testid="theme-toggle-mobile">
          {theme === "dark" ? <><Sun size={14} /> Switch to light</> : <><Moon size={14} /> Switch to dark</>}
        </button>
      </div>
    </div>
  );
}

export default function NavBar({ onOpenAdvisor }) {
  const [active, setActive] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();

  useEffect(() => {
    setMobileOpen(false);
    setActive(null);
  }, [location]);

  const openGroup = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActive(label);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setActive(null), 180);
  };

  return (
    <header className="sticky top-0 z-50 glass-header" data-testid="site-header">
      <div className="sg-stripe" />
      <div className="container-rp">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="relative">
              <div className="font-display text-[22px] font-bold tracking-tight" style={{ color: "var(--rp-primary)" }}>
                RiskPedia
              </div>
              <div className="absolute -right-2 -top-1 w-1.5 h-1.5 rounded-full" style={{ background: "var(--rp-accent)" }} />
            </div>
            <span className="text-[11px] font-mono px-2 py-0.5 rounded-sm" style={{ background: "var(--rp-primary)", color: "#fff", letterSpacing: "0.1em" }}>SG</span>
          </Link>

          <nav className="hidden lg:flex items-center" onMouseLeave={scheduleClose}>
            {NAV_GROUPS.map(g => (
              <div
                key={g.label}
                className="relative"
                onMouseEnter={() => openGroup(g.label)}
              >
                <button
                  className="px-4 h-16 flex items-center gap-1 text-[13px] font-medium text-slate-700 hover:text-slate-900 transition-colors"
                  data-testid={`nav-group-${g.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {g.label}
                  <ChevronDown size={14} className={`transition-transform ${active === g.label ? "rotate-180" : ""}`} />
                </button>
                {active === g.label && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 fade-in" onMouseEnter={() => openGroup(g.label)} onMouseLeave={scheduleClose}>
                    <MegaPanel group={g} onClose={() => setActive(null)} />
                  </div>
                )}
              </div>
            ))}
            <Link to="/enterprise" className="px-4 h-16 flex items-center text-[13px] font-medium text-slate-700 hover:text-slate-900 transition-colors" data-testid="nav-enterprise">Enterprise</Link>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <GlobalSearch />
            <button onClick={toggle} className="theme-toggle" data-testid="theme-toggle" aria-label="Toggle dark mode" title={theme === "dark" ? "Switch to light" : "Switch to dark"}>
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {user ? (
              <UserMenu user={user} logout={logout} />
            ) : (
              <Link to="/login" className="persona-btn" data-testid="nav-login"><User size={14} /> Sign in</Link>
            )}

            <button onClick={onOpenAdvisor} className="btn-accent" data-testid="nav-open-advisor">
              <Sparkles size={14} /> Ask AI Advisor
            </button>
          </div>

          <button className="lg:hidden p-2" onClick={() => setMobileOpen(v => !v)} data-testid="nav-mobile-toggle">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && <MobileMenu onOpenAdvisor={onOpenAdvisor} theme={theme} toggle={toggle} />}
    </header>
  );
}
